/**
 * MemberService
 * Service for Member entity operations
 * Route: api/members
 * Controller: MembersController.cs
 */
import { CCMSError } from '../../models/common/CCMSError';
import { CrudService } from '../base/CrudService';
/**
 * MemberService
 * Handles member CRUD operations and custom endpoints
 * Maps to /api/members endpoint
 */
export class MemberService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MemberService.DEFAULT_SERVICE_PATH,
            basePath: MemberService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== ADDRESSES (admin-on-behalf) ====================
    /** All addresses for a member, resolved by their UserId. */
    async getAddressesByUserId(userId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('addressesByUser', `by-user/${userId}/addresses`)));
        return res.data ?? [];
    }
    /** Add an address to a member, resolved by their UserId. */
    async addAddressForUser(userId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addAddressForUser', `by-user/${userId}/addresses`)), data);
        if (!res.data)
            throw new Error('Failed to add address');
        return res.data;
    }
    /** Update one of a member's addresses, resolved by their UserId. */
    async updateAddressForUser(userId, addressId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateAddressForUser', `by-user/${userId}/addresses/${addressId}`)), data);
        if (!res.data)
            throw new Error('Failed to update address');
        return res.data;
    }
    /** Delete one of a member's addresses, resolved by their UserId. */
    async deleteAddressForUser(userId, addressId) {
        await this.http.delete(this.buildUrl(this.getEndpoint('deleteAddressForUser', `by-user/${userId}/addresses/${addressId}`)));
    }
    // ==================== CUSTOM QUERY METHODS ====================
    /**
     * Get paged list of members with optional filtering
     * GET /api/members?page={page}&pageSize={pageSize}&isActive={isActive}&typeId={typeId}
     * @param page - Page number (default: 1)
     * @param pageSize - Page size (default: 50)
     * @param isActive - Filter by active status
     * @param typeId - Filter by member type ID
     * @returns Array of members
     */
    async getPaged(page = 1, pageSize = 50, isActive, typeId) {
        const response = await this.getPagedRaw(page, pageSize, isActive, typeId);
        return response.data ?? [];
    }
    /**
     * Get paged list of members (raw)
     * @param page - Page number
     * @param pageSize - Page size
     * @param isActive - Filter by active status
     * @param typeId - Filter by member type ID
     * @returns ResponseData with array of members
     */
    getPagedRaw(page = 1, pageSize = 50, isActive, typeId) {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        if (isActive !== null && isActive !== undefined) {
            params.append('isActive', isActive.toString());
        }
        if (typeId !== null && typeId !== undefined) {
            params.append('typeId', typeId.toString());
        }
        const endpoint = this.getEndpoint('getPaged', `?${params.toString()}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get member by user ID
     * GET /api/members/by-user/{userId}
     * @param userId - User ID
     * @returns Member object
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        if (!response.data) {
            throw new Error(`Member with user ID ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get member by user ID (raw)
     * @param userId - User ID
     * @returns ResponseData with member
     */
    getByUserIdRaw(userId) {
        const endpoint = this.getEndpoint('byUserId', `by-user/${userId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get members with birthdays today
     * GET /api/members/birthdays
     * @returns Array of members with birthdays today
     */
    async getBirthdays() {
        const response = await this.getBirthdaysRaw();
        return response.data ?? [];
    }
    /**
     * Get members with birthdays today (raw)
     * @returns ResponseData with array of members
     */
    getBirthdaysRaw() {
        const endpoint = this.getEndpoint('birthdays', 'birthdays');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Search members by a single term across first name, surname, middle name, email,
     * cellphone, alternate contact, member ID, user ID and member code (case-insensitive
     * substring match).
     * GET /api/members/search?term={term}&limit={limit}
     * @param term - Search term (required)
     * @param limit - Max results (default 50, max 200)
     * @returns Array of matching members
     */
    async searchMembers(term, limit = 50) {
        const response = await this.searchMembersRaw(term, limit);
        return response.data ?? [];
    }
    /**
     * Search members (raw)
     * @param term - Search term
     * @param limit - Max results
     * @returns ResponseData with array of matching members
     */
    searchMembersRaw(term, limit = 50) {
        const params = new URLSearchParams();
        params.append('term', term);
        params.append('limit', limit.toString());
        const endpoint = this.getEndpoint('searchMembers', `search?${params.toString()}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CUSTOM UPDATE METHODS ====================
    /**
     * Update member active status
     * PATCH /api/members/{id}/status
     * @param id - Member ID
     * @param isActive - New active status
     * @returns Updated member
     */
    async updateStatus(id, isActive) {
        const response = await this.updateStatusRaw(id, isActive);
        if (!response.data) {
            throw new Error(`Failed to update member ${id} status`);
        }
        return response.data;
    }
    /**
     * Update member status (raw)
     * @param id - Member ID
     * @param isActive - New active status
     * @returns ResponseData with updated member
     */
    updateStatusRaw(id, isActive) {
        const endpoint = this.getEndpoint('updateStatus', `${id}/status`);
        const url = this.buildUrl(endpoint);
        return this.http.patch(url, isActive);
    }
    // ==================== MODELVIEW METHODS ====================
    /**
     * Get member with enriched model view by ID (cache-first)
     * GET /api/members/view/{id}
     * @param id - Member ID
     * @returns Enriched member model view with type and marital status
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Member view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get member view by ID (raw)
     * @param id - Member ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all member views (cache-first, enriched with type and marital status)
     * GET /api/members/view
     * @returns Array of enriched member model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all member views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * The admin member directory: server-side search, filters, sorting and paging.
     * POST /api/members/list-view
     * @param filter - search / typeId(s) / maritalStatusId / isActive / isMember / hasLogin /
     *   locationId (0 = Online Campus) / includeServeLocation / includeDeleted / page / pageSize / sortBy
     * @returns One page of enriched member views with totals
     */
    async listView(filter) {
        const response = await this.listViewRaw(filter);
        return response.data ?? emptyPage(filter?.pageSize ?? 25);
    }
    /** POST /api/members/list-view (raw). */
    listViewRaw(filter) {
        const endpoint = this.getEndpoint('listView', 'list-view');
        return this.http.post(this.buildUrl(endpoint), filter || {});
    }
    /**
     * Filtered member views — kept for existing callers; returns just the rows of one page
     * (the endpoint is paged since 2026-10: pass `page` / `pageSize`, default 25).
     * Prefer {@link listView}, which also returns the totals.
     */
    async getFilteredViews(filter) {
        const response = await this.getFilteredViewsRaw(filter);
        const data = response.data;
        if (Array.isArray(data))
            return data; // pre-2026-10 servers returned a plain array
        return data?.items ?? [];
    }
    /** POST /api/members/list-view (raw, paged). */
    getFilteredViewsRaw(filter) {
        return this.listViewRaw(filter);
    }
    // ==================== DUPLICATE CHECK ====================
    /**
     * Find a live member with the same first name AND surname (case-insensitive) AND the same
     * email or cellphone. GET /api/members/check-duplicate
     * @returns The duplicate, or null when none exists (the API answers 404)
     */
    async checkDuplicate(firstName, surname, email, cellphone) {
        try {
            const url = this.buildUrlWithParams(this.getEndpoint('checkDuplicate', 'check-duplicate'), {
                firstName,
                surname,
                email: email || undefined,
                cellphone: cellphone || undefined,
            });
            const response = await this.http.get(url);
            return response.data ?? null;
        }
        catch (e) {
            if (e instanceof CCMSError && e.status === 404)
                return null;
            throw e;
        }
    }
    // ==================== HR / ADMIN BY USER ID ====================
    /**
     * Partial update of a member's profile by their UserId (HR / admin).
     * PUT /api/members/by-user/{userId}. Same fields as PUT /api/members/me; send `null` to clear
     * a field, omit it to leave it unchanged.
     */
    async updateMemberByUserId(userId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateByUser', `by-user/${encodeURIComponent(userId)}`)), data);
        if (!res.data)
            throw new Error('Failed to update member');
        return res.data;
    }
    /** GET /api/members/by-user/{userId}/allergies (empty when the user has no member row yet). */
    async getAllergiesByUserId(userId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('allergiesByUser', `by-user/${encodeURIComponent(userId)}/allergies`)));
        return res.data ?? [];
    }
    /** POST /api/members/by-user/{userId}/allergies (idempotent: an existing link just gets the note). */
    async addAllergyForUser(userId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addAllergyForUser', `by-user/${encodeURIComponent(userId)}/allergies`)), data);
        if (!res.data)
            throw new Error('Failed to add allergy');
        return res.data;
    }
    /** PUT /api/members/by-user/{userId}/allergies/{allergyId} (note only). */
    async updateAllergyForUser(userId, allergyId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateAllergyForUser', `by-user/${encodeURIComponent(userId)}/allergies/${allergyId}`)), data);
        if (!res.data)
            throw new Error('Failed to update allergy');
        return res.data;
    }
    /** DELETE /api/members/by-user/{userId}/allergies/{allergyId}. */
    async deleteAllergyForUser(userId, allergyId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteAllergyForUser', `by-user/${encodeURIComponent(userId)}/allergies/${allergyId}`)));
        return res.success;
    }
    /** GET /api/members/by-user/{userId}/disabilities (empty when the user has no member row yet). */
    async getDisabilitiesByUserId(userId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('disabilitiesByUser', `by-user/${encodeURIComponent(userId)}/disabilities`)));
        return res.data ?? [];
    }
    /** POST /api/members/by-user/{userId}/disabilities. */
    async addDisabilityForUser(userId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addDisabilityForUser', `by-user/${encodeURIComponent(userId)}/disabilities`)), data);
        if (!res.data)
            throw new Error('Failed to add disability');
        return res.data;
    }
    /** PUT /api/members/by-user/{userId}/disabilities/{disabilityId} (note only). */
    async updateDisabilityForUser(userId, disabilityId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateDisabilityForUser', `by-user/${encodeURIComponent(userId)}/disabilities/${disabilityId}`)), data);
        if (!res.data)
            throw new Error('Failed to update disability');
        return res.data;
    }
    /** DELETE /api/members/by-user/{userId}/disabilities/{disabilityId}. */
    async deleteDisabilityForUser(userId, disabilityId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteDisabilityForUser', `by-user/${encodeURIComponent(userId)}/disabilities/${disabilityId}`)));
        return res.success;
    }
    // ==================== BY MEMBER ID (members without a login too) ====================
    /** GET /api/members/{id}/addresses */
    async getMemberAddresses(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('memberAddresses', `${memberId}/addresses`)));
        return res.data ?? [];
    }
    /** POST /api/members/{id}/addresses */
    async addMemberAddress(memberId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addMemberAddress', `${memberId}/addresses`)), data);
        if (!res.data)
            throw new Error('Failed to add address');
        return res.data;
    }
    /** PUT /api/members/{id}/addresses/{addressId} — omitted fields unchanged, "" clears. */
    async updateMemberAddress(memberId, addressId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateMemberAddress', `${memberId}/addresses/${addressId}`)), data);
        if (!res.data)
            throw new Error('Failed to update address');
        return res.data;
    }
    /** DELETE /api/members/{id}/addresses/{addressId} */
    async deleteMemberAddress(memberId, addressId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteMemberAddress', `${memberId}/addresses/${addressId}`)));
        return res.success;
    }
    /** GET /api/members/{id}/allergies */
    async getMemberAllergies(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('memberAllergies', `${memberId}/allergies`)));
        return res.data ?? [];
    }
    /** POST /api/members/{id}/allergies */
    async addMemberAllergy(memberId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addMemberAllergy', `${memberId}/allergies`)), data);
        if (!res.data)
            throw new Error('Failed to add allergy');
        return res.data;
    }
    /** PUT /api/members/{id}/allergies/{allergyId} */
    async updateMemberAllergy(memberId, allergyId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateMemberAllergy', `${memberId}/allergies/${allergyId}`)), data);
        if (!res.data)
            throw new Error('Failed to update allergy');
        return res.data;
    }
    /** DELETE /api/members/{id}/allergies/{allergyId} */
    async deleteMemberAllergy(memberId, allergyId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteMemberAllergy', `${memberId}/allergies/${allergyId}`)));
        return res.success;
    }
    /** GET /api/members/{id}/disabilities */
    async getMemberDisabilities(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('memberDisabilities', `${memberId}/disabilities`)));
        return res.data ?? [];
    }
    /** POST /api/members/{id}/disabilities */
    async addMemberDisability(memberId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addMemberDisability', `${memberId}/disabilities`)), data);
        if (!res.data)
            throw new Error('Failed to add disability');
        return res.data;
    }
    /** PUT /api/members/{id}/disabilities/{disabilityId} */
    async updateMemberDisability(memberId, disabilityId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateMemberDisability', `${memberId}/disabilities/${disabilityId}`)), data);
        if (!res.data)
            throw new Error('Failed to update disability');
        return res.data;
    }
    /** DELETE /api/members/{id}/disabilities/{disabilityId} */
    async deleteMemberDisability(memberId, disabilityId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteMemberDisability', `${memberId}/disabilities/${disabilityId}`)));
        return res.success;
    }
    /** GET /api/members/{id}/families — every family the member is in, with all members. */
    async getMemberFamilies(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('memberFamilies', `${memberId}/families`)));
        return res.data ?? [];
    }
    /** GET /api/members/{id}/locations — home + serve campus (0 = Online Campus). */
    async getMemberLocations(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('memberLocations', `${memberId}/locations`)));
        return res.data ?? [];
    }
    /** PUT /api/members/{id}/locations — replace the campus (`locationID: null` removes it). */
    async setMemberLocation(memberId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('setMemberLocation', `${memberId}/locations`)), data);
        return res.data ?? [];
    }
    /**
     * Members at a campus (Locations module "Members at this campus"), paged with names.
     * GET /api/members/by-location/{locationId}. `locationId` 0 is "Online Campus".
     */
    async getMembersByLocation(locationId, options = {}) {
        const url = this.buildUrlWithParams(this.getEndpoint('membersByLocation', `by-location/${locationId}`), {
            includeServe: options.includeServe,
            search: options.search || undefined,
            isActive: options.isActive,
            page: options.page,
            pageSize: options.pageSize,
        });
        const res = await this.http.get(url);
        return res.data ?? emptyPage(options.pageSize ?? 25);
    }
    // ==================== NOTES (admin only) ====================
    /** GET /api/members/{id}/notes — newest first. */
    async getNotes(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('notes', `${memberId}/notes`)));
        return res.data ?? [];
    }
    /** POST /api/members/{id}/notes */
    async addNote(memberId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addNote', `${memberId}/notes`)), data);
        if (!res.data)
            throw new Error('Failed to add note');
        return res.data;
    }
    /** PUT /api/members/{id}/notes/{noteId} */
    async updateNote(memberId, noteId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateNote', `${memberId}/notes/${noteId}`)), data);
        if (!res.data)
            throw new Error('Failed to update note');
        return res.data;
    }
    /** DELETE /api/members/{id}/notes/{noteId} */
    async deleteNote(memberId, noteId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteNote', `${memberId}/notes/${noteId}`)));
        return res.success;
    }
    // ==================== EMERGENCY CONTACTS ====================
    /** GET /api/members/{id}/emergency-contacts — primary first. */
    async getEmergencyContacts(memberId) {
        const res = await this.http.get(this.buildUrl(this.getEndpoint('emergencyContacts', `${memberId}/emergency-contacts`)));
        return res.data ?? [];
    }
    /** POST /api/members/{id}/emergency-contacts (name + phone required). */
    async addEmergencyContact(memberId, data) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('addEmergencyContact', `${memberId}/emergency-contacts`)), data);
        if (!res.data)
            throw new Error('Failed to add emergency contact');
        return res.data;
    }
    /** PUT /api/members/{id}/emergency-contacts/{contactId} */
    async updateEmergencyContact(memberId, contactId, data) {
        const res = await this.http.put(this.buildUrl(this.getEndpoint('updateEmergencyContact', `${memberId}/emergency-contacts/${contactId}`)), data);
        if (!res.data)
            throw new Error('Failed to update emergency contact');
        return res.data;
    }
    /** DELETE /api/members/{id}/emergency-contacts/{contactId} */
    async deleteEmergencyContact(memberId, contactId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('deleteEmergencyContact', `${memberId}/emergency-contacts/${contactId}`)));
        return res.success;
    }
    // ==================== AUDIT ====================
    /** GET /api/members/{id}/audit?limit= — field-level change history, newest first (limit 1-1000, default 200). */
    async getAudit(memberId, limit) {
        const url = this.buildUrlWithParams(this.getEndpoint('audit', `${memberId}/audit`), { limit });
        const res = await this.http.get(url);
        return res.data ?? [];
    }
    // ==================== LOGIN LINK / DELETE / MERGE ====================
    /**
     * Link the member to a login. POST /api/members/{id}/link-user.
     * Throws (409) when the member has a different login or the login belongs to another member.
     */
    async linkUser(memberId, userId) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('linkUser', `${memberId}/link-user`)), { userId });
        if (!res.data)
            throw new Error('Failed to link login');
        return res.data;
    }
    /** Remove the member's login link. POST /api/members/{id}/unlink-user */
    async unlinkUser(memberId) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('unlinkUser', `${memberId}/unlink-user`)), {});
        if (!res.data)
            throw new Error('Failed to unlink login');
        return res.data;
    }
    /**
     * Soft-delete a member (DELETE /api/members/{id}). Throws (409) while the member is linked to a
     * login — unlink or merge first. Use {@link restore} to undo.
     */
    async softDelete(memberId) {
        const res = await this.http.delete(this.buildUrl(String(memberId)));
        if (!res.data)
            throw new Error('Failed to delete member');
        return res.data;
    }
    /** Undo a soft delete. POST /api/members/{id}/restore */
    async restore(memberId) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('restore', `${memberId}/restore`)), {});
        if (!res.data)
            throw new Error('Failed to restore member');
        return res.data;
    }
    /**
     * Merge member `otherId` INTO `memberId`. POST /api/members/{id}/merge/{otherId}.
     * The result lists the other modules that may still reference `otherId` (not rewritten).
     */
    async merge(memberId, otherId) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('merge', `${memberId}/merge/${otherId}`)), {});
        if (!res.data)
            throw new Error('Failed to merge members');
        return res.data;
    }
    // ==================== PLANNING CENTRE ====================
    /** GET /api/members/{id}/planning-centre/candidates?q= (at least 2 characters). */
    async getPlanningCentreCandidates(memberId, q) {
        const url = this.buildUrlWithParams(this.getEndpoint('pcCandidates', `${memberId}/planning-centre/candidates`), { q });
        const res = await this.http.get(url);
        return res.data ?? [];
    }
    /** POST /api/members/{id}/planning-centre/link */
    async linkPlanningCentre(memberId, planningCentrePersonId) {
        const res = await this.http.post(this.buildUrl(this.getEndpoint('pcLink', `${memberId}/planning-centre/link`)), { planningCentrePersonId });
        if (!res.data)
            throw new Error('Failed to link Planning Centre');
        return res.data;
    }
    /** DELETE /api/members/{id}/planning-centre/link */
    async unlinkPlanningCentre(memberId) {
        const res = await this.http.delete(this.buildUrl(this.getEndpoint('pcUnlink', `${memberId}/planning-centre/link`)));
        if (!res.data)
            throw new Error('Failed to unlink Planning Centre');
        return res.data;
    }
    /**
     * Get member views by member type (cache-first)
     * GET /api/members/type/{typeId}/view
     * @param typeId - Member type ID
     * @returns Array of enriched member model views
     */
    async getViewsByType(typeId) {
        const response = await this.getViewsByTypeRaw(typeId);
        return response.data ?? [];
    }
    /**
     * Get member views by type (raw)
     * @param typeId - Member type ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByTypeRaw(typeId) {
        const endpoint = this.getEndpoint('viewsByType', `type/${typeId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get member views by marital status (cache-first)
     * GET /api/members/marital-status/{statusId}/view
     * @param statusId - Marital status ID
     * @returns Array of enriched member model views
     */
    async getViewsByMaritalStatus(statusId) {
        const response = await this.getViewsByMaritalStatusRaw(statusId);
        return response.data ?? [];
    }
    /**
     * Get member views by marital status (raw)
     * @param statusId - Marital status ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByMaritalStatusRaw(statusId) {
        const endpoint = this.getEndpoint('viewsByMaritalStatus', `marital-status/${statusId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
MemberService.DEFAULT_SERVICE_PATH = 'members';
MemberService.BASE_PATH = 'api/members';
function emptyPage(pageSize) {
    return { items: [], totalCount: 0, page: 1, pageSize, totalPages: 0, hasNextPage: false, hasPreviousPage: false };
}
//# sourceMappingURL=MemberService.js.map